import { AxiosError } from "axios";
import { useMessagesStore } from "../store/useMessagesStore";
import { enrichmentApi } from "./api";
import { BrandEnrichmentConfig, EnrichmentExportParams, EnrichmentExportScope } from "../types/enrichment";
import { AsyncJob, AsyncJobsResponse, AsyncJobStatus, AsyncJobType } from "../types/async-jobs";

const { addError, addSuccess } = useMessagesStore.getState();

interface UploadUrlResponse {
  jobId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface EnrichedPersonasResponse {
  data: Record<string, unknown>[];
  total: number;
}

export const apiCreateEnrichmentConfig = async (
  brandId: string
): Promise<BrandEnrichmentConfig | null> => {
  try {
    const result = await enrichmentApi.post<BrandEnrichmentConfig>(
      `/enrichment/${brandId}/config`
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to create enrichment config for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

export const apiGetEnrichmentConfig = async (
  brandId: string
): Promise<BrandEnrichmentConfig | null> => {
  try {
    const result = await enrichmentApi.get<BrandEnrichmentConfig>(
      `/enrichment/${brandId}/config`
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to fetch enrichment config for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

export const apiUpdateEnrichmentConfig = async (
  brandId: string,
  config: BrandEnrichmentConfig
): Promise<BrandEnrichmentConfig | null> => {
  try {
    const result = await enrichmentApi.put<BrandEnrichmentConfig>(
      `/enrichment/${brandId}/config`,
      config
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to update enrichment config for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

export const apiGetEnrichedPersonas = async (
  brandId: string,
  limit: number = 50,
  offset: number = 0
): Promise<EnrichedPersonasResponse | null> => {
  try {
    const result = await enrichmentApi.get<EnrichedPersonasResponse>(
      `/enrichment/${brandId}/data`,
      { params: { limit, offset } }
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to fetch enriched data for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

/**
 * Check if the enrichment verification service is active for a brand.
 * Calls the billing endpoint and looks for the "enrichment" service.
 */
export const apiIsEnrichmentActive = async (
  brandId: string
): Promise<boolean> => {
  try {
    // const result = await enrichmentApi.get<{
    //   verificationServices: { id: string; status: string }[];
    // }>(`/billing/${brandId}`);
    // const svc = result.data?.verificationServices?.find(
    //   (s) => s.id === "enrichment"
    // );
    // return svc?.status === "active";
    // ONLY FOR TESTING
    return true;
  } catch {
    return false;
  }
};

export const apiRequestUploadUrl = async (
  brandId: string,
  fileName: string,
  contentType: string
): Promise<UploadUrlResponse | null> => {
  try {
    const result = await enrichmentApi.post<UploadUrlResponse>(
      `/enrichment/${brandId}/upload`,
      { fileName, contentType }
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to request upload URL for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

export const apiConfirmUpload = async (
  jobId: string
): Promise<boolean> => {
  try {
    await enrichmentApi.post(`/enrichment/jobs/${jobId}/confirm`);
    return true;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to confirm upload for job ${jobId}: ${message || "Unknown error"}`);
    return false;
  }
};

export const apiEnrichmentExport = async (
  brandId: string,
  params: EnrichmentExportParams
): Promise<AsyncJob | null> => {
  const body: EnrichmentExportParams = {
    scope: params.scope,
    outputFormat: params.outputFormat,
  };
  if (params.scope === EnrichmentExportScope.CUSTOM) {
    body.dateFrom = params.dateFrom;
    body.dateTo = params.dateTo;
  }
  if (params.includeChildren) {
    body.includeChildren = true;
  }

  try {
    const result = await enrichmentApi.post<AsyncJob>(`/enrichment/${brandId}/export`, body);
    addSuccess("Enrichment export started");
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to start enrichment export for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

export const apiGetEnrichmentJobs = async (
  brandId: string,
  params: {
    limit?: number;
    offset?: number;
    type?: AsyncJobType;
    status?: AsyncJobStatus;
  } = {},
): Promise<AsyncJobsResponse | null> => {
  try {
    const result = await enrichmentApi.get<AsyncJobsResponse>(
      `/enrichment/${brandId}/jobs`,
      { params },
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to fetch enrichment jobs for brand ${brandId}: ${message || "Unknown error"}`);
    return null;
  }
};

export const apiGetEnrichmentJob = async (jobId: string): Promise<AsyncJob | null> => {
  try {
    const result = await enrichmentApi.get<AsyncJob>(
      `/enrichment/jobs/${jobId}`
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to fetch enrichment job ${jobId}: ${message || "Unknown error"}`);
    return null;
  }
};

interface DownloadUrlResponse {
  jobId: string;
  downloadUrl: string;
  downloadExpiresAt: string;
}

export const apiDeleteEnrichmentJob = async (jobId: string): Promise<boolean> => {
  try {
    await enrichmentApi.delete(`/enrichment/jobs/${jobId}`);
    return true;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to delete job ${jobId}: ${message || "Unknown error"}`);
    return false;
  }
};

export const apiGetDownloadUrl = async (jobId: string): Promise<DownloadUrlResponse | null> => {
  try {
    const result = await enrichmentApi.get<DownloadUrlResponse>(
      `/enrichment/jobs/${jobId}/download`
    );
    return result.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    const message = axiosError?.response?.data?.message;
    addError(`Failed to download file for job ${jobId}: ${message || "Unknown error"}`);
    return null;
  }
};